import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SESSION_TOKEN_KEY = 'propflow_session_token';

export function useSessionTracking() {
  const hasTracked = useRef(false);

  useEffect(() => {
    const trackSession = async () => {
      try {
        if (hasTracked.current) return;

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        // Skip if token expires within 60 seconds
        if (session.expires_at && (session.expires_at * 1000) - Date.now() < 60000) {
          console.debug('Session token expiring soon, skipping tracking.');
          return;
        }

        hasTracked.current = true;

        let sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
        if (!sessionToken) {
          sessionToken = crypto.randomUUID();
          localStorage.setItem(SESSION_TOKEN_KEY, sessionToken);
        }

        try {
          await supabase.functions.invoke('track-session', {
            body: { userAgent: navigator.userAgent, sessionToken },
          });
        } catch {
          // Silently ignore - non-critical telemetry
          console.debug('Session tracking call skipped');
        }
      } catch {
        console.debug('Session tracking skipped');
      }
    };

    trackSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        hasTracked.current = false;
        trackSession();
      } else if (event === 'SIGNED_OUT') {
        hasTracked.current = false;
        localStorage.removeItem(SESSION_TOKEN_KEY);
      }
    });

    return () => subscription.unsubscribe();
  }, []);
}

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}
